import React, { HTMLProps, RefObject, useState } from 'react'
import Button from '../../../components/Button/Button'
import Input, { InputType } from '../../../components/Input'
import { Color, Direction } from '../../../utils/constants'
import { useForm } from 'react-hook-form'
import api from '../../../utils/api'
import FadeIn from '../../../components/Animation/FadeIn'
import ReCAPTCHA, { onVerify } from '../../../components/ReCAPTCHAWrapper'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'

interface ContactFormProps extends HTMLProps<HTMLDivElement> {
  scrollRef: RefObject<HTMLDivElement>
}

const ContactForm = ({ scrollRef, ...props }: ContactFormProps) => {
    const [error, setError] = useState<string | null>(null)
    const [success, setSuccess] = useState(null)
    const { executeRecaptcha } = useGoogleReCaptcha()

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({
        mode: 'onChange',
    })
    const onSubmit = async (data: any) => {
        const submitData = () =>
            api
                .post('/wp-json/contact-form/v1/contact-us', data)
                .then(({ data }) => {
                    if (data.status === 'mail_sent') {
                        setSuccess(data.message)
                    } else {
                        setError(data.message ?? 'An issue occurred. Please try again.')
                    }
                })
                .catch((e) => setError(e.message))
        await onVerify({ callback: submitData, executeRecaptcha, setError })
    }

    const getErrorMessage = (type?: string, name?: string) => {
        if (type) {
            switch (type) {
            case 'required':
                return 'This field is required'
            case 'pattern':
                return `Please enter valid ${name}`
            }
        }
        return undefined
    }
    return (
        <div className='xl:absolute xl:right-0 bottom-0 md:z-50' {...props}>
            <FadeIn appearFromBottom>
                <div
                    className='bg-white pt-10 md:p-10 xl:p-16 border-t md:border border-black/10 '
                    ref={scrollRef}
                >
                    <h3 className='text-center'>Contact Form</h3>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className='grid grid-cols-2 gap-4 md:w-[440px] py-10'>
                            <Input
                                label='first name'
                                name='firstName'
                                register={register}
                                validationPattern={new RegExp('^[^0-9]+$')}
                                required
                                errorMessage={getErrorMessage(errors.firstName?.type as string, 'first name')}
                                placeholder='Type in first name...'
                            />
                            <Input
                                label='last name'
                                name='lastName'
                                validationPattern={new RegExp('^[^0-9]+$')}
                                register={register}
                                required
                                errorMessage={getErrorMessage(errors.lastName?.type as string, 'last name')}
                                placeholder='Type in last name...'
                            />
                            <div className='col-span-2 md:col-span-1'>
                                <Input
                                    label='job title'
                                    name='jobTitle'
                                    register={register}
                                    required
                                    errorMessage={getErrorMessage(errors.jobTitle?.type as string)}
                                    placeholder='Type in job title...'
                                />
                            </div>
                            <div className='col-span-2 md:col-span-1'>
                                <Input
                                    label='email'
                                    name='email'
                                    register={register}
                                    validationPattern={
                                        new RegExp('^[A-Za-z0-9_!#$%&\'*+/=?`{|}~^.-]+@[A-Za-z0-9.-]+$')
                                    }
                                    required
                                    errorMessage={getErrorMessage(errors.email?.type as string, 'email')}
                                    placeholder='Type in email...'
                                />
                            </div>
                            <Input
                                label='company name'
                                name='companyName'
                                className='col-span-2'
                                register={register}
                                required
                                errorMessage={getErrorMessage(errors.companyName?.type as string)}
                                placeholder='Type in company name...'
                            />
                            <Input
                                label='enquiry type'
                                name='enquiryType'
                                className='col-span-2'
                                register={register}
                                options={[
                                    { id: 'partnering', value: 'Partnering' },
                                    { id: 'press', value: 'Press' },
                                    { id: 'careers', value: 'Careers' },
                                    { id: 'others', value: 'Others' },
                                ]}
                                inputType={InputType.SELECT}
                                required
                                errorMessage={getErrorMessage(errors.enquiryType?.type as string)}
                                placeholder='Select enquiry type...'
                            />
                            <Input
                                label='message'
                                name='message'
                                className='col-span-2'
                                register={register}
                                required
                                inputType={InputType.TEXT_AREA}
                                errorMessage={getErrorMessage(errors.message?.type as string)}
                                placeholder='Type in message...'
                            />
                        </div>
                        <Button
                            isInverted
                            buttonColor={Color.PURPLE}
                            outerClasses='w-full mt-4 ga-cta'
                            type='submit'
                            chevron={Direction.RIGHT}
                            id='contact-us-form'
                        >
                            <h6>Submit</h6>
                        </Button>
                        {success && <p className='text-center pt-2'>{success}</p>}
                        {error && <p className='text-red text-center pt-2'>{error}</p>}
                    </form>
                </div>
            </FadeIn>
        </div>
    )
}

const ContactFormWrapper = (props: ContactFormProps) => {
    return (
        <ReCAPTCHA>
            <ContactForm {...props} />
        </ReCAPTCHA>
    )
}

export default ContactFormWrapper
